import React, { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "@/utils/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Shield, User, AlertCircle, LogOut, DollarSign, CheckCircle, Clock, AlertTriangle, FileText, Upload, QrCode, MessageSquare, Send, Paperclip, Image, File, Download, Menu, X, CreditCard, Settings, Globe, Camera, Trash2, Megaphone, Siren, Briefcase, Edit, Lock, ArrowLeft } from "lucide-react";
import { getSavedLanguage, getTranslation } from "@/utils/translations";
import useContentTranslation from "@/utils/useContentTranslation";


function ProfileView({ user, t, onUserUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(user.profile_photo || null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: user.full_name || "",
    phone: user.phone || "",
    nationality: user.nationality || "",
    employer: user.employer || "",
    visa_expiry: user.visa_expiry || ""
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const fileInputRef = React.useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error("Please upload a JPG or PNG image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        const response = await axios.post('/profile/photo', {
          photo_data: base64Data
        });
        
        if (response.data.success) {
          setProfilePhoto(response.data.photo_url);
          if (onUserUpdate) {
            onUserUpdate({ ...user, profile_photo: response.data.photo_url });
          }
          toast.success("Profile photo updated!");
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!confirm("Are you sure you want to remove your profile photo?")) return;
    
    setUploading(true);
    try {
      await axios.delete('/profile/photo');
      setProfilePhoto(null);
      if (onUserUpdate) {
        onUserUpdate({ ...user, profile_photo: null });
      }
      toast.success("Profile photo removed");
    } catch (error) {
      toast.error("Failed to remove photo");
    } finally {
      setUploading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put('/profile', editForm);
      if (response.data.success) {
        if (onUserUpdate) {
          onUserUpdate(response.data.user);
        }
        toast.success("Profile updated successfully!");
        setIsEditDialogOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post('/profile/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      if (response.data.success) {
        toast.success("Password changed successfully!");
        setIsPasswordDialogOpen(false);
        setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-view">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight">{t('member.profile.title')}</h2>
          <p className="text-white/50 text-sm mt-1">Your personal information and membership details</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogTrigger asChild>
              <button 
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 transition-colors"
                data-testid="edit-profile-button"
              >
                <Edit className="w-4 h-4" />
                Edit Profile
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a2e] border-white/10 rounded-3xl max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white text-xl">Edit Profile</DialogTitle>
                <DialogDescription className="text-white/50">
                  Update your personal information. Email and passport number cannot be changed.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Full Name</Label>
                  <Input
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Phone Number</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                    placeholder="+60 12 345 6789"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Nationality</Label>
                  <Input
                    value={editForm.nationality}
                    onChange={(e) => setEditForm({...editForm, nationality: e.target.value})}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                    placeholder="Your nationality"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Employer</Label>
                  <Input
                    value={editForm.employer}
                    onChange={(e) => setEditForm({...editForm, employer: e.target.value})}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                    placeholder="Your employer"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Visa Expiry Date</Label>
                  <Input
                    type="date"
                    value={editForm.visa_expiry}
                    onChange={(e) => setEditForm({...editForm, visa_expiry: e.target.value})}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                  />
                </div>
                
                {/* Read-only fields notice */}
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
                  <p className="text-amber-400 text-xs">
                    <strong>Note:</strong> Email ({user.email}) and Passport Number ({user.passport_number || "N/A"}) cannot be changed for security reasons.
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditDialogOpen(false)}
                    className="flex-1 border-white/10 text-white hover:bg-white/10 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
            <DialogTrigger asChild>
              <button 
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 transition-colors"
                data-testid="change-password-button"
              >
                <Lock className="w-4 h-4" />
                Change Password
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a2e] border-white/10 rounded-3xl max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white text-xl">Change Password</DialogTitle>
                <DialogDescription className="text-white/50">
                  Enter your current password and choose a new password.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handlePasswordSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Current Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                    placeholder="Enter current password"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                    placeholder="Enter new password (min 6 characters)"
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsPasswordDialogOpen(false)}
                    className="flex-1 border-white/10 text-white hover:bg-white/10 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl"
                  >
                    {saving ? "Changing..." : "Change Password"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Hero Profile Card */}
      <div className="glass-card overflow-hidden animate-slide-up">
        <div className="bg-gradient-to-br from-cyan-500/20 via-blue-600/10 to-purple-500/20 p-8">
          <div className="flex items-center gap-6">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full opacity-70 blur-sm group-hover:opacity-100 transition-opacity"></div>
              <Avatar className="relative h-28 w-28 ring-4 ring-white/20">
                {profilePhoto ? (
                  <AvatarImage src={profilePhoto} alt={user.full_name} className="object-cover" />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-3xl font-bold">
                  {user.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                </AvatarFallback>
              </Avatar>
              {/* Upload overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2 rounded-full">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="p-2.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors active:scale-95"
                  title="Upload photo"
                >
                  <Camera className="w-5 h-5 text-white" strokeWidth={1.5} />
                </button>
                {profilePhoto && (
                  <button
                    onClick={handleDeletePhoto}
                    disabled={uploading}
                    className="p-2.5 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors active:scale-95"
                    title="Remove photo"
                  >
                    <Trash2 className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white tracking-tight">{user.full_name}</h1>
              <p className="text-white/60 text-lg mt-1">{user.email}</p>
              <div className="flex items-center gap-3 mt-3">
                <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
                  user.subscription_status === "active" 
                    ? "bg-emerald-500/20 text-emerald-400 glow-green" 
                    : "bg-amber-500/20 text-amber-400"
                }`}>
                  {user.subscription_status === "active" ? (t('status.active') || "Active") : (t('status.pending') || "Pending")}
                </span>
                {uploading && <span className="text-xs text-white/40">Uploading...</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Info Card */}
        <div className="glass-card p-6 animate-slide-up stagger-1" style={{ opacity: 0 }}>
          <h3 className="font-semibold text-lg text-white mb-5 flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-xl">
              <User className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
            </div>
            {t('member.profile.personalInfo')}
          </h3>
          <div className="space-y-4">
            <GlassInfoRow label={t('member.profile.phone')} value={user.phone || "N/A"} />
            <GlassInfoRow label={t('member.profile.passportNumber')} value={user.passport_number || "N/A"} />
            <GlassInfoRow label={t('member.profile.nationality')} value={user.nationality || "N/A"} />
          </div>
        </div>

        {/* Employment Details Card */}
        <div className="glass-card p-6 animate-slide-up stagger-2" style={{ opacity: 0 }}>
          <h3 className="font-semibold text-lg text-white mb-5 flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-xl">
              <FileText className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
            </div>
            {t('member.profile.employmentDetails') || 'Employment Details'}
          </h3>
          <div className="space-y-4">
            <GlassInfoRow label={t('member.profile.employer')} value={user.employer || "N/A"} />
            <GlassInfoRow label={t('member.profile.visaExpiry')} value={user.visa_expiry || "N/A"} />
            <div className="flex items-center justify-between py-3 border-b border-white/5">
              <span className="text-sm text-white/50">{t('member.profile.subscriptionStatus')}:</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                user.subscription_status === "active" 
                  ? "bg-emerald-500/20 text-emerald-400" 
                  : "bg-red-500/20 text-red-400"
              }`}>
                {user.subscription_status === "active" ? (t('status.active') || "Active") : (t('status.pending') || "Pending")}
              </span>
            </div>
            <GlassInfoRow label={t('member.profile.monthlyFee') || 'Monthly Fee'} value={`RM ${user.monthly_fee || 0}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function GlassInfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <span className="text-sm text-white/50">{label}</span>
      <span className="text-sm font-medium text-white">{value}</span>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">{label}:</span>
      <span className="text-sm font-medium text-slate-900">{value}</span>
    </div>
  );
}

// SOS Emergency View
function SOSView({ user, t }) {
  const [sosHistory, setSOSHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  useEffect(() => {
    fetchSOSHistory();
  }, []);

  const fetchSOSHistory = async () => {
    try {
      const response = await axios.get('/sos-alerts');
      setSOSHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch SOS history', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendSOS = async () => {
    setSending(true);
    try {
      await axios.post('/sos-alerts', {});
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      fetchSOSHistory();
      toast.success(t('member.sos.successTitle'));
    } catch (error) {
      toast.error('Failed to send SOS alert');
    } finally {
      setSending(false);
    }
  };

  const [holdProgress, setHoldProgress] = useState(0);
  const holdTimerRef = React.useRef(null);
  const holdStartRef = React.useRef(null);

  const startHold = () => {
    holdStartRef.current = Date.now();
    holdTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - holdStartRef.current;
      const progress = Math.min((elapsed / 3000) * 100, 100);
      setHoldProgress(progress);
      
      if (progress >= 100) {
        clearInterval(holdTimerRef.current);
        setShowConfirmDialog(true);
        setHoldProgress(0);
      }
    }, 50);
  };

  const endHold = () => {
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
      setHoldProgress(0);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-red-500/20 text-red-400 glow-red',
      in_progress: 'bg-amber-500/20 text-amber-400',
      resolved: 'bg-emerald-500/20 text-emerald-400 glow-green'
    };
    return styles[status] || 'bg-white/10 text-white/60';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="sos-view">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white tracking-tight">{t('member.sos.title')}</h2>
        <p className="text-white/50 text-sm mt-1">{t('member.sos.callDirectly')}</p>
      </div>

      {/* Main SOS Button - Apple Watch Style */}
      <div className="glass-card p-8 animate-slide-up relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-transparent to-red-900/10"></div>
        
        <div className="relative text-center">
          <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
            {/* Giant SOS Button */}
            <div className="relative inline-flex items-center justify-center mx-auto mb-8">
              {/* Pulsing Rings */}
              <div className="absolute w-40 h-40 rounded-full border-2 border-red-500/30 animate-ring-pulse"></div>
              <div className="absolute w-48 h-48 rounded-full border border-red-500/20 animate-ring-pulse" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute w-56 h-56 rounded-full border border-red-500/10 animate-ring-pulse" style={{ animationDelay: '1s' }}></div>
              
              {/* Progress Ring */}
              <svg className="absolute w-36 h-36" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="rgba(255,59,48,0.2)"
                  strokeWidth="4"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#FF3B30"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${holdProgress * 2.83} 283`}
                  transform="rotate(-90 50 50)"
                  className="transition-all duration-100"
                />
              </svg>
              
              {/* Main Button */}
              <button
                className={`relative w-32 h-32 rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white font-bold flex flex-col items-center justify-center transition-all duration-200 active:scale-95 ${
                  holdProgress > 0 ? 'scale-105 animate-pulse-glow' : 'shadow-[0_0_30px_rgba(255,59,48,0.5)]'
                }`}
                onPointerDown={startHold}
                onPointerUp={endHold}
                onPointerLeave={endHold}
                onPointerCancel={endHold}
                data-testid="sos-button"
              >
                <Siren className="w-10 h-10 mb-1" strokeWidth={1.5} />
                <span className="text-sm font-bold tracking-wider">SOS</span>
              </button>
            </div>
            
            <p className="text-white/60 text-sm mb-2">{t('member.sos.holdToSend') || 'Hold for 3 seconds to send SOS'}</p>
            <div className="w-48 h-1 bg-white/10 rounded-full mx-auto overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-500 to-red-400 transition-all duration-100"
                style={{ width: `${holdProgress}%` }}
              ></div>
            </div>
            
            {/* Confirm Dialog */}
            <DialogContent className="max-w-md bg-slate-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="text-red-400 flex items-center gap-2">
                  <Siren className="w-6 h-6" strokeWidth={1.5} />
                  {t('member.sos.confirmTitle')}
                </DialogTitle>
                <DialogDescription className="text-white/60 pt-2">
                  {t('member.sos.confirmMessage')}
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmDialog(false)}
                  className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
                  disabled={sending}
                >
                  {t('member.sos.confirmNo')}
                </Button>
                <Button
                  onClick={handleSendSOS}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white border-0"
                  disabled={sending}
                  data-testid="confirm-sos-button"
                >
                  {sending ? (
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    t('member.sos.confirmYes')
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Success Dialog */}
          <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
            <DialogContent className="max-w-md bg-slate-900 border-white/10 text-white text-center">
              <div className="py-4">
                <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 glow-green">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('member.sos.successTitle')}</h3>
                <p className="text-white/60">{t('member.sos.successMessage')}</p>
                <p className="text-sm text-emerald-400 font-medium mt-4">{t('member.sos.stayCalm')}</p>
              </div>
              <Button onClick={() => setShowSuccessDialog(false)} className="w-full bg-white/10 hover:bg-white/20 border-0">
                OK
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Emergency Contacts */}
      <div className="glass-card overflow-hidden animate-slide-up stagger-1" style={{ opacity: 0 }}>
        <div className="p-4 border-b border-white/5">
          <h3 className="font-semibold text-white">{t('member.sos.emergencyContacts')}</h3>
        </div>
        <div className="divide-y divide-white/5">
          {[
            { name: t('member.sos.myshieldHotline'), number: '+60 19-988 8720', tel: 'tel:+60199888720', icon: 'phone', color: 'cyan' },
            { name: t('member.sos.labourDept'), number: '1800-88-8088', tel: 'tel:1800888088', icon: 'users', color: 'amber' },
            { name: t('member.sos.police'), number: '999', tel: 'tel:999', icon: 'shield', color: 'red' },
            { name: t('member.sos.ambulance'), number: '999', tel: 'tel:999', icon: 'heart', color: 'emerald' }
          ].map((contact, idx) => (
            <a key={idx} href={contact.tel} className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors active:scale-[0.99]">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 bg-${contact.color}-500/20 rounded-2xl flex items-center justify-center`}>
                  {contact.icon === 'phone' && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={`text-${contact.color}-400`}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>}
                  {contact.icon === 'users' && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={`text-${contact.color}-400`}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
                  {contact.icon === 'shield' && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={`text-${contact.color}-400`}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
                  {contact.icon === 'heart' && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={`text-${contact.color}-400`}><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>}
                </div>
                <div>
                  <p className="font-medium text-white">{contact.name}</p>
                  <p className="text-sm text-white/50">{contact.number}</p>
                </div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/30"><path d="m9 18 6-6-6-6"/></svg>
            </a>
          ))}
        </div>
      </div>

      {/* SOS History */}
      <div className="glass-card overflow-hidden animate-slide-up stagger-2" style={{ opacity: 0 }}>
        <div className="p-4 border-b border-white/5">
          <h3 className="font-semibold text-white">{t('member.sos.sosHistory')}</h3>
        </div>
        <div>
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-white/20 border-t-cyan-400 rounded-full animate-spin mx-auto"></div>
            </div>
          ) : sosHistory.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-white/20" strokeWidth={1.5} />
              </div>
              <p className="text-white/40">{t('member.sos.noHistory')}</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {sosHistory.map((alert) => (
                <div key={alert.id} className="p-4 flex items-center justify-between" data-testid={`sos-history-${alert.id}`}>
                  <div>
                    <p className="font-medium text-white">{formatDate(alert.created_at)}</p>
                    {alert.message && <p className="text-sm text-white/50">{alert.message}</p>}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(alert.status)}`}>
                    {t(`member.sos.status.${alert.status}`)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Vacancies View
function VacanciesView({ user, t }) {
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' or 'applications'
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [applying, setApplying] = useState(false);
  const [filters, setFilters] = useState({
    industry: '',
    location: '',
    jobType: '',
    search: ''
  });
  const [industries, setIndustries] = useState([]);
  const [locations, setLocations] = useState([]);
  const { translateTexts, translating } = useContentTranslation();
  const [translatedContent, setTranslatedContent] = useState({});
  const [currentLang, setCurrentLang] = useState(getSavedLanguage());
  
  // Poll for language changes
  useEffect(() => {
    const checkLanguage = () => {
      const lang = getSavedLanguage();
      if (lang !== currentLang) {
        setCurrentLang(lang);
        setTranslatedContent({}); // Clear translations to trigger re-fetch
      }
    };
    const interval = setInterval(checkLanguage, 500);
    return () => clearInterval(interval);
  }, [currentLang]);

  useEffect(() => {
    fetchJobs();
    fetchApplications();
    fetchFilters();
  }, []);

  // Auto-translate job content when jobs change or language changes
  useEffect(() => {
    if (currentLang !== 'en' && jobs.length > 0) {
      const textsToTranslate = [];
      jobs.forEach(job => {
        textsToTranslate.push(job.title);
        if (job.company_name) textsToTranslate.push(job.company_name);
        if (job.description) textsToTranslate.push(job.description);
        if (job.requirements) textsToTranslate.push(job.requirements);
      });
      
      // Also translate applications
      applications.forEach(app => {
        if (app.job_title) textsToTranslate.push(app.job_title);
        if (app.company_name) textsToTranslate.push(app.company_name);
      });
      
      if (textsToTranslate.length > 0) {
        translateTexts(textsToTranslate).then(translations => {
          setTranslatedContent(translations);
        });
      }
    } else if (currentLang === 'en') {
      setTranslatedContent({});
    }
  }, [jobs, applications, currentLang, translateTexts]);

  const getTranslated = (text) => {
    if (currentLang === 'en' || !text) return text;
    return translatedContent[text] || text;
  };

  const fetchJobs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.industry) params.append('industry', filters.industry);
      if (filters.location) params.append('location', filters.location);
      if (filters.jobType) params.append('job_type', filters.jobType);
      
      const response = await axios.get(`/jobs?${params.toString()}`);
      let filteredJobs = response.data;
      
      // Client-side search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredJobs = filteredJobs.filter(job => 
          job.title.toLowerCase().includes(searchLower) ||
          job.company_name.toLowerCase().includes(searchLower) ||
          job.location.toLowerCase().includes(searchLower)
        );
      }
      
      setJobs(filteredJobs);
    } catch (error) {
      console.error('Failed to fetch jobs', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get('/job-applications');
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications', error);
    }
  };

  const fetchFilters = async () => {
    try {
      const [indRes, locRes] = await Promise.all([
        axios.get('/jobs/industries'),
        axios.get('/jobs/locations')
      ]);
      setIndustries(indRes.data.industries || []);
      setLocations(locRes.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch filters', error);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [filters.industry, filters.location, filters.jobType]);

  const handleApply = async () => {
    if (!selectedJob) return;
    setApplying(true);
    try {
      await axios.post('/job-applications', {
        job_id: selectedJob.id,
        cover_letter: coverLetter
      });
      toast.success(t('member.vacancies.applicationSent'));
      setShowApplyDialog(false);
      setCoverLetter('');
      fetchApplications();
      setSelectedJob(null);
    } catch (error) {
      if (error.response?.data?.detail === 'You have already applied for this job') {
        toast.error(t('member.vacancies.alreadyApplied'));
      } else {
        toast.error('Failed to apply');
      }
    } finally {
      setApplying(false);
    }
  };

  const getJobTypeBadge = (type) => {
    const styles = {
      full_time: 'bg-emerald-100 text-emerald-700',
      part_time: 'bg-blue-100 text-blue-700',
      contract: 'bg-amber-100 text-amber-700',
      temporary: 'bg-slate-100 text-slate-700'
    };
    return styles[type] || 'bg-slate-100 text-slate-700';
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: 'bg-blue-100 text-blue-700',
      reviewing: 'bg-amber-100 text-amber-700',
      interview: 'bg-purple-100 text-purple-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return null;
    if (min && max) return `RM ${min.toLocaleString()} - ${max.toLocaleString()}`;
    if (min) return `RM ${min.toLocaleString()}+`;
    return `Up to RM ${max.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const hasApplied = (jobId) => {
    return applications.some(app => app.job_id === jobId);
  };

  const clearFilters = () => {
    setFilters({ industry: '', location: '', jobType: '', search: '' });
  };

  // Job Detail View
  if (selectedJob) {
    const applied = hasApplied(selectedJob.id);
    
    return (
      <div className="space-y-6" data-testid="job-detail-view">
        <button
          onClick={() => setSelectedJob(null)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
          data-testid="back-to-jobs"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          {t('member.vacancies.backToJobs')}
        </button>

        <Card className="rounded-sm border-slate-200 shadow-sm overflow-hidden">
          <CardHeader className="bg-slate-50 border-b border-slate-200">
            <div className="flex justify-between items-start">
              <div>
                <Badge className={`${getJobTypeBadge(selectedJob.job_type)} rounded-sm font-semibold mb-2`}>
                  {t(`member.vacancies.jobType.${selectedJob.job_type}`)}
                </Badge>
                <CardTitle className="text-2xl font-bold text-slate-900">{getTranslated(selectedJob.title)}</CardTitle>
                <p className="text-lg text-slate-600 mt-1">{getTranslated(selectedJob.company_name)}</p>
              </div>
              {applied ? (
                <Badge className="bg-emerald-100 text-emerald-700 rounded-sm font-semibold text-sm px-4 py-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  {t('member.vacancies.applied')}
                </Badge>
              ) : (
                <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-slate-900 hover:bg-slate-800 px-6" data-testid="apply-now-btn">
                      {t('member.vacancies.applyNow')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{t('member.vacancies.confirmApply')}</DialogTitle>
                      <DialogDescription>
                        {t('member.vacancies.confirmApplyMessage')}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <Label>{t('member.vacancies.coverLetter')}</Label>
                      <textarea
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                        placeholder={t('member.vacancies.coverLetterPlaceholder')}
                        className="w-full mt-2 p-3 border border-slate-200 rounded-lg resize-none h-32"
                        data-testid="cover-letter-input"
                      />
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowApplyDialog(false)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={handleApply} disabled={applying} data-testid="confirm-apply-btn">
                        {applying ? (
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          t('member.vacancies.apply')
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                <span>{selectedJob.location}</span>
              </div>
              {formatSalary(selectedJob.salary_min, selectedJob.salary_max) && (
                <div className="flex items-center gap-2 text-slate-600">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  <span>{formatSalary(selectedJob.salary_min, selectedJob.salary_max)} {t('member.vacancies.perMonth')}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-slate-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <span>{t('member.vacancies.postedOn')} {formatDate(selectedJob.created_at)}</span>
              </div>
              {selectedJob.application_deadline && (
                <div className="flex items-center gap-2 text-red-600">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>{t('member.vacancies.deadline')}: {formatDate(selectedJob.application_deadline)}</span>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">{t('member.vacancies.description')}</h3>
              <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">{getTranslated(selectedJob.description)}</p>
            </div>

            {selectedJob.requirements && (
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{t('member.vacancies.requirements')}</h3>
                <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">{getTranslated(selectedJob.requirements)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vacancies-view">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white tracking-tight">{t('member.vacancies.title')}</h2>
        <p className="text-white/50 text-sm mt-1">Browse available job opportunities</p>
      </div>

      {/* Tab Navigation - Apple HIG Pills */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('jobs')}
          className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 active:scale-95 ${
            activeTab === 'jobs' 
              ? 'bg-white/15 text-white' 
              : 'text-white/50 hover:bg-white/5 hover:text-white/70'
          }`}
          data-testid="tab-jobs"
        >
          {t('member.vacancies.title')} ({jobs.length})
        </button>
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 active:scale-95 ${
            activeTab === 'applications' 
              ? 'bg-white/15 text-white' 
              : 'text-white/50 hover:bg-white/5 hover:text-white/70'
          }`}
          data-testid="tab-applications"
        >
          {t('member.vacancies.myApplications')} ({applications.length})
        </button>
      </div>

      {activeTab === 'jobs' && (
        <>
          {/* Filters - Horizontal Scrollable Pills */}
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder={t('member.vacancies.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-full focus:ring-cyan-500/50"
                data-testid="job-search-input"
              />
            </div>
            <Select value={filters.industry || "all"} onValueChange={(val) => setFilters(prev => ({ ...prev, industry: val === "all" ? "" : val }))}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white rounded-full" data-testid="industry-filter">
                <SelectValue placeholder={t('member.vacancies.allIndustries')} />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                <SelectItem value="all" className="text-white">{t('member.vacancies.allIndustries')}</SelectItem>
                    {industries.map(ind => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.jobType || "all"} onValueChange={(val) => setFilters(prev => ({ ...prev, jobType: val === "all" ? "" : val }))}>
                  <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white rounded-full" data-testid="type-filter">
                    <SelectValue placeholder={t('member.vacancies.allTypes')} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    <SelectItem value="all" className="text-white">{t('member.vacancies.allTypes')}</SelectItem>
                    <SelectItem value="full_time" className="text-white">{t('member.vacancies.jobType.full_time')}</SelectItem>
                    <SelectItem value="part_time" className="text-white">{t('member.vacancies.jobType.part_time')}</SelectItem>
                    <SelectItem value="contract" className="text-white">{t('member.vacancies.jobType.contract')}</SelectItem>
                    <SelectItem value="temporary" className="text-white">{t('member.vacancies.jobType.temporary')}</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" onClick={clearFilters} className="text-white/50 hover:text-white hover:bg-white/5 rounded-full" data-testid="clear-filters">
                  {t('member.vacancies.clearFilters')}
                </Button>
          </div>

          {/* Job Cards */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-white/20 border-t-cyan-400 rounded-full animate-spin mx-auto"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-white/20" strokeWidth={1.5} />
              </div>
              <p className="text-white/40">{t('member.vacancies.noJobs')}</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {jobs.map((job, idx) => (
                <div 
                  key={job.id} 
                  className="glass-card p-5 hover:bg-white/10 transition-all duration-300 cursor-pointer active:scale-[0.99] animate-slide-up"
                  style={{ opacity: 0, animationDelay: `${idx * 0.1}s` }}
                  onClick={() => setSelectedJob(job)}
                  data-testid={`job-card-${job.id}`}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex gap-4 flex-1">
                      {/* Company Logo Placeholder */}
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-7 h-7 text-cyan-400" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                            job.job_type === 'full_time' ? 'bg-emerald-500/20 text-emerald-400' :
                            job.job_type === 'part_time' ? 'bg-amber-500/20 text-amber-400' :
                            job.job_type === 'contract' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {t(`member.vacancies.jobType.${job.job_type}`)}
                          </span>
                          {hasApplied(job.id) && (
                            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-400 glow-green">
                              {t('member.vacancies.applied')}
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-lg text-white truncate">{getTranslated(job.title)}</h3>
                        <p className="text-white/60">{getTranslated(job.company_name)}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-white/40">
                          <span className="flex items-center gap-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                            {job.location}
                          </span>
                          {formatSalary(job.salary_min, job.salary_max) && (
                            <span className="flex items-center gap-1.5 text-cyan-400">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                              {formatSalary(job.salary_min, job.salary_max)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button className="p-2 hover:bg-white/10 rounded-full transition-colors" data-testid={`view-job-${job.id}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/40"><path d="m9 18 6-6-6-6"/></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'applications' && (
        <div className="space-y-4">
          {applications.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-white/20" strokeWidth={1.5} />
              </div>
              <p className="text-white/40">{t('member.vacancies.noApplications')}</p>
            </div>
          ) : (
            applications.map((app, idx) => (
              <div key={app.id} className="glass-card p-5 animate-slide-up" style={{ opacity: 0, animationDelay: `${idx * 0.1}s` }} data-testid={`application-${app.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-lg text-white">{getTranslated(app.job_title)}</h3>
                    <p className="text-white/60">{getTranslated(app.company_name)}</p>
                    <p className="text-sm text-white/40 mt-2">
                      {t('member.vacancies.postedOn')} {formatDate(app.applied_at)}
                    </p>
                  </div>
                  <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                    app.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400 glow-green' :
                    app.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    app.status === 'interview' ? 'bg-purple-500/20 text-purple-400' :
                    app.status === 'reviewing' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>
                    {t(`member.vacancies.applicationStatus.${app.status}`)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ComplaintsView({ user, t }) {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "general",
    priority: "medium",
  });

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      const response = await axios.get('/complaints');
      setComplaints(response.data);
    } catch (error) {
      toast.error("Failed to load complaints");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/complaints', formData);
      toast.success("Complaint submitted successfully");
      setIsDialogOpen(false);
      setFormData({ title: "", description: "", category: "general", priority: "medium" });
      fetchComplaints();
    } catch (error) {
      toast.error("Failed to submit complaint");
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "pending": return { icon: <Clock className="w-4 h-4" />, color: "from-amber-500 to-orange-500", bg: "bg-amber-500/20", text: "text-amber-400" };
      case "in_progress": return { icon: <AlertTriangle className="w-4 h-4" />, color: "from-blue-500 to-cyan-500", bg: "bg-blue-500/20", text: "text-blue-400" };
      case "resolved": return { icon: <CheckCircle className="w-4 h-4" />, color: "from-emerald-500 to-green-500", bg: "bg-emerald-500/20", text: "text-emerald-400" };
      default: return { icon: <AlertCircle className="w-4 h-4" />, color: "from-slate-500 to-gray-500", bg: "bg-slate-500/20", text: "text-slate-400" };
    }
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case "critical": return { color: "from-red-500 to-rose-500", bg: "bg-red-500/20", text: "text-red-400" };
      case "high": return { color: "from-orange-500 to-amber-500", bg: "bg-orange-500/20", text: "text-orange-400" };
      case "medium": return { color: "from-yellow-500 to-amber-400", bg: "bg-yellow-500/20", text: "text-yellow-400" };
      case "low": return { color: "from-green-500 to-emerald-500", bg: "bg-green-500/20", text: "text-green-400" };
      default: return { color: "from-slate-500 to-gray-500", bg: "bg-slate-500/20", text: "text-slate-400" };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="complaints-view">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white tracking-tight">{t('member.complaints.title')}</h2>
        <p className="text-white/50 text-sm mt-1">Report issues and track their resolution</p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4 animate-slide-up">
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-amber-400">{complaints.filter(c => c.status === 'pending').length}</div>
          <div className="text-xs text-white/50 mt-1">Pending</div>
        </div>
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-blue-400">{complaints.filter(c => c.status === 'in_progress').length}</div>
          <div className="text-xs text-white/50 mt-1">In Progress</div>
        </div>
        <div className="glass-card p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">{complaints.filter(c => c.status === 'resolved').length}</div>
          <div className="text-xs text-white/50 mt-1">Resolved</div>
        </div>
      </div>

      {/* New Complaint Button */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <button 
            data-testid="new-complaint-button" 
            className="w-full glass-card p-4 flex items-center justify-center gap-3 hover:bg-white/10 transition-all duration-300 group animate-slide-up stagger-1"
            style={{ opacity: 0 }}
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold">+ {t('member.complaints.newComplaint')}</span>
          </button>
        </DialogTrigger>
        <DialogContent data-testid="complaint-dialog" className="bg-[#1a1a2e] border-white/10 rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">{t('member.complaints.newComplaint')}</DialogTitle>
            <DialogDescription className="text-white/50">
              {t('member.complaints.submitDescription') || 'Describe your issue and we\'ll get back to you soon.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-white/70 text-sm">{t('member.complaints.complaintTitle')}</Label>
              <Input
                id="title"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl focus:ring-cyan-500"
                placeholder={t('member.complaints.titlePlaceholder') || 'Brief description of the issue'}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="complaint-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-white/70 text-sm">{t('member.complaints.description')}</Label>
              <Textarea
                id="description"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl focus:ring-cyan-500 min-h-[120px]"
                placeholder={t('member.complaints.descriptionPlaceholder') || 'Provide detailed information about your complaint'}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                data-testid="complaint-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category" className="text-white/70 text-sm">{t('member.complaints.category')}</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger data-testid="complaint-category-select" className="bg-white/5 border-white/10 text-white rounded-xl">
                    <SelectValue placeholder={t('member.complaints.selectCategory') || 'Select category'} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-white/10">
                    <SelectItem value="general">{t('categories.general') || 'General'}</SelectItem>
                    <SelectItem value="workplace">{t('categories.workplace') || 'Workplace'}</SelectItem>
                    <SelectItem value="salary">{t('categories.salary') || 'Salary'}</SelectItem>
                    <SelectItem value="harassment">{t('categories.harassment') || 'Harassment'}</SelectItem>
                    <SelectItem value="safety">{t('categories.safety') || 'Safety'}</SelectItem>
                    <SelectItem value="other">{t('categories.other') || 'Other'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority" className="text-white/70 text-sm">{t('member.complaints.priority')}</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger data-testid="complaint-priority-select" className="bg-white/5 border-white/10 text-white rounded-xl">
                    <SelectValue placeholder={t('member.complaints.selectPriority') || 'Select priority'} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-white/10">
                    <SelectItem value="low">{t('priority.low') || 'Low'}</SelectItem>
                    <SelectItem value="medium">{t('priority.medium') || 'Medium'}</SelectItem>
                    <SelectItem value="high">{t('priority.high') || 'High'}</SelectItem>
                    <SelectItem value="critical">{t('priority.critical') || 'Critical'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <button 
              type="submit" 
              className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:opacity-90 transition-opacity"
              data-testid="submit-complaint-button"
            >
              {t('member.complaints.submitComplaint')}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complaints List */}
      <div className="space-y-4">
        {complaints.map((complaint, idx) => {
          const statusConfig = getStatusConfig(complaint.status);
          const priorityConfig = getPriorityConfig(complaint.priority);
          return (
            <div 
              key={complaint.id} 
              className="glass-card p-5 animate-slide-up hover:bg-white/[0.08] transition-all duration-300"
              style={{ opacity: 0, animationDelay: `${(idx + 2) * 0.1}s` }}
              data-testid={`member-complaint-card-${complaint.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${statusConfig.color} flex items-center justify-center shrink-0`}>
                  {statusConfig.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="text-white font-semibold truncate">{complaint.title}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                      {complaint.status.replace("_", " ")}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityConfig.bg} ${priorityConfig.text}`}>
                      {complaint.priority || "medium"}
                    </span>
                    {complaint.escalated && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                        Escalated
                      </span>
                    )}
                  </div>
                  <p className="text-white/60 text-sm line-clamp-2 mb-2">{complaint.description}</p>
                  <div className="flex items-center gap-3 text-xs text-white/40">
                    <span className="capitalize">{complaint.category}</span>
                    <span>•</span>
                    <span>{new Date(complaint.created_at).toLocaleDateString()}</span>
                    {complaint.deadline && (
                      <>
                        <span>•</span>
                        <span className="text-amber-400">Due: {new Date(complaint.deadline).toLocaleDateString()}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {complaints.length === 0 && (
          <div className="glass-card p-12 text-center animate-slide-up">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-white font-semibold mb-1">No Complaints</h3>
            <p className="text-white/50 text-sm">{t('member.complaints.noComplaints')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PaymentsView({ user, t }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [manualPaymentData, setManualPaymentData] = useState({
    reference_number: "",
    proof_image: ""
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const DUITNOW_QR = "https://customer-assets.emergentagent.com/job_protect-workers/artifacts/qjoiqcjn_image.png";

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get('/payments');
      setPayments(response.data);
    } catch (error) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async (method) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post('/payment/create-session', {
        package_id: method === "subscription" ? "subscription" : "1",
        payment_method: method,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment session");
      setProcessingPayment(false);
    }
  };

  const handleManualPaymentSubmit = async () => {
    if (!manualPaymentData.reference_number) {
      toast.error("Please enter the reference number");
      return;
    }
    
    setProcessingPayment(true);
    try {
      const response = await axios.post('/payment/create-session', {
        package_id: "1",
        payment_method: selectedMethod,
        origin_url: window.location.origin
      });
      
      await axios.post('/payment/submit-manual', {
        transaction_id: response.data.transaction_id,
        reference_number: manualPaymentData.reference_number,
        proof_image: manualPaymentData.proof_image
      });
      
      toast.success("Payment submitted for verification!");
      setSelectedMethod(null);
      setManualPaymentData({ reference_number: "", proof_image: "" });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-view">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white tracking-tight">{t('member.payments.myPayments') || 'My Payments'}</h2>
        <p className="text-white/50 text-sm mt-1">Manage your subscription and make payments</p>
      </div>

      {/* Current Subscription Card */}
      <div className="glass-card overflow-hidden animate-slide-up">
        <div className="bg-gradient-to-br from-cyan-500/20 via-blue-600/10 to-purple-500/20 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm uppercase tracking-wide font-medium">{t('member.payments.monthlySubscription') || 'Monthly Subscription'}</p>
              <p className="text-4xl font-bold text-white mt-2">
                <span className="text-lg font-normal text-white/60">RM</span> 20
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
              user.subscription_status === "active" 
                ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30" 
                : "bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30"
            }`}>
              {user.subscription_status === "active" ? "Active" : "Pending"}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Options */}
      {!selectedMethod ? (
        <div className="space-y-3 animate-slide-up stagger-1" style={{ opacity: 0 }}>
          <p className="text-white/60 text-sm font-medium mb-4">{t('member.payments.chooseMethod') || 'Choose payment method'}</p>
          
          {/* Card Payment */}
          <button
            onClick={() => handleStripePayment("fpx")}
            disabled={processingPayment}
            className="w-full glass-card p-5 flex items-center gap-4 text-left hover:bg-white/10 transition-all duration-300 disabled:opacity-50 group"
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-105 transition-transform">
              <CreditCard className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-white">{t('member.payments.cardPayment') || 'Card Payment'}</div>
              <div className="text-sm text-white/50">{t('member.payments.cardPaymentDesc') || 'One-time payment with debit/credit card'}</div>
            </div>
            <div className="text-xl font-bold text-white">RM 20</div>
          </button>

          {/* Auto-Debit Subscription */}
          <button
            onClick={() => handleStripePayment("subscription")}
            disabled={processingPayment}
            className="w-full glass-card p-5 flex items-center gap-4 text-left hover:bg-white/10 transition-all duration-300 disabled:opacity-50 group"
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-500/20 group-hover:scale-105 transition-transform">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-white">{t('member.payments.autoDebit') || 'Auto-Debit Monthly'}</div>
              <div className="text-sm text-white/50">{t('member.payments.autoDebitDesc') || 'Automatic monthly payment - cancel anytime'}</div>
            </div>
            <div className="text-xl font-bold text-white">RM 20<span className="text-sm text-white/50">/mo</span></div>
          </button>

          {/* DuitNow QR */}
          <button
            onClick={() => setSelectedMethod("qr_code")}
            disabled={processingPayment}
            className="w-full glass-card p-5 flex items-center gap-4 text-left hover:bg-white/10 transition-all duration-300 disabled:opacity-50 group"
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-500/20 group-hover:scale-105 transition-transform">
              <QrCode className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-white">DuitNow QR</div>
              <div className="text-sm text-white/50">{t('member.payments.qrDesc') || 'Scan and pay with any banking app'}</div>
            </div>
            <div className="text-xl font-bold text-white">RM 20</div>
          </button>

          {/* Bank Transfer */}
          <button
            onClick={() => setSelectedMethod("bank_transfer")}
            disabled={processingPayment}
            className="w-full glass-card p-5 flex items-center gap-4 text-left hover:bg-white/10 transition-all duration-300 disabled:opacity-50 group"
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20 group-hover:scale-105 transition-transform">
              <DollarSign className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-white">{t('member.payments.bankTransfer') || 'Bank Transfer'}</div>
              <div className="text-sm text-white/50">{t('member.payments.bankTransferDesc') || 'Manual transfer to bank account'}</div>
            </div>
            <div className="text-xl font-bold text-white">RM 20</div>
          </button>
        </div>
      ) : selectedMethod === "qr_code" ? (
        /* QR Code Payment View */
        <div className="glass-card p-6 animate-slide-up">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">DuitNow QR Payment</h3>
            <button onClick={() => setSelectedMethod(null)} className="text-white/60 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="text-center mb-6">
            <div className="inline-block p-4 bg-white rounded-2xl">
              <img src={DUITNOW_QR} alt="DuitNow QR" className="w-56 h-56" />
            </div>
            <p className="mt-4 text-2xl font-bold text-white">RM 20</p>
            <p className="text-white/50 text-sm">{t('member.payments.scanWithApp') || 'Scan with your banking app'}</p>
          </div>
          <div className="border-t border-white/10 pt-6 space-y-4">
            <p className="text-white/70 text-sm">{t('member.payments.afterPayment') || 'After payment, enter details below:'}</p>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">{t('member.payments.referenceNumber') || 'Reference Number'} *</Label>
              <Input
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                placeholder={t('member.payments.enterReference') || 'Enter transaction reference'}
                value={manualPaymentData.reference_number}
                onChange={(e) => setManualPaymentData({...manualPaymentData, reference_number: e.target.value})}
              />
            </div>
            <button 
              onClick={handleManualPaymentSubmit} 
              disabled={processingPayment}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {processingPayment ? "Submitting..." : (t('member.payments.submitProof') || "Submit Payment Proof")}
            </button>
          </div>
        </div>
      ) : (
        /* Bank Transfer View */
        <div className="glass-card p-6 animate-slide-up">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">{t('member.payments.bankTransferPayment') || 'Bank Transfer Payment'}</h3>
            <button onClick={() => setSelectedMethod(null)} className="text-white/60 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="bg-white/5 rounded-2xl p-5 mb-6 space-y-3">
            <h4 className="font-semibold text-white mb-3">{t('member.payments.bankDetails') || 'Bank Account Details'}</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-white/50">{t('member.payments.bank') || 'Bank'}:</span>
                <span className="text-white font-medium">Maybank</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-white/50">{t('member.payments.accountName') || 'Account Name'}:</span>
                <span className="text-white font-medium">MyShield Sdn Bhd</span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5">
                <span className="text-white/50">{t('member.payments.accountNumber') || 'Account Number'}:</span>
                <span className="text-white font-medium">5123 4567 8901</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-white/50">{t('member.payments.amount') || 'Amount'}:</span>
                <span className="text-emerald-400 font-bold">RM 20</span>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 space-y-4">
            <p className="text-white/70 text-sm">{t('member.payments.afterTransfer') || 'After transfer, enter details below:'}</p>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">{t('member.payments.referenceNumber') || 'Reference Number'} *</Label>
              <Input
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                placeholder={t('member.payments.enterReference') || 'Enter transaction reference'}
                value={manualPaymentData.reference_number}
                onChange={(e) => setManualPaymentData({...manualPaymentData, reference_number: e.target.value})}
              />
            </div>
            <button 
              onClick={handleManualPaymentSubmit} 
              disabled={processingPayment}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {processingPayment ? "Submitting..." : (t('member.payments.submitProof') || "Submit Payment Proof")}
            </button>
          </div>
        </div>
      )}

      {/* Payment History */}
      <div className="glass-card p-5 animate-slide-up stagger-2" style={{ opacity: 0 }}>
        <h3 className="text-lg font-semibold text-white mb-4">{t('member.payments.title')}</h3>
        {payments.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-3">
              <DollarSign className="w-6 h-6 text-white/30" />
            </div>
            <p className="text-white/50 text-sm">{t('member.payments.noHistory') || 'No payment history yet'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map((payment, idx) => (
              <div 
                key={payment.id} 
                className="flex items-center justify-between p-4 bg-white/5 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    payment.status === "paid" ? "bg-emerald-500/20" : payment.status === "pending" ? "bg-amber-500/20" : "bg-red-500/20"
                  }`}>
                    {payment.status === "paid" ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    ) : payment.status === "pending" ? (
                      <Clock className="w-5 h-5 text-amber-400" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-semibold">RM {payment.amount}</p>
                    <p className="text-white/50 text-xs">{new Date(payment.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  payment.status === "paid" ? "bg-emerald-500/20 text-emerald-400" : 
                  payment.status === "pending" ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"
                }`}>
                  {payment.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Documents View
function DocumentsView({ user, t }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    document_type: "passport",
    document_name: "",
    document_data: "",
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          document_name: file.name,
          document_data: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.document_data) {
      toast.error("Please select a file to upload");
      return;
    }
    setUploading(true);
    try {
      await axios.post('/documents', formData);
      toast.success("Document uploaded successfully");
      setIsDialogOpen(false);
      setFormData({ document_type: "passport", document_name: "", document_data: "" });
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const getDocumentIcon = (type) => {
    switch(type) {
      case "passport": return "bg-gradient-to-br from-blue-500 to-indigo-600";
      case "work_permit": return "bg-gradient-to-br from-emerald-500 to-teal-600";
      case "contract": return "bg-gradient-to-br from-amber-500 to-orange-600";
      default: return "bg-gradient-to-br from-slate-500 to-gray-600";
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      passport: t('member.documents.passport'),
      work_permit: t('member.documents.visa'),
      contract: t('member.documents.contract'),
      other: t('member.documents.other'),
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-view">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white tracking-tight">{t('member.documents.title')}</h2>
        <p className="text-white/50 text-sm mt-1">Store and manage your important documents securely</p>
      </div>

      {/* Upload Button */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <button 
            data-testid="upload-document-button" 
            className="w-full glass-card p-4 flex items-center justify-center gap-3 hover:bg-white/10 transition-all duration-300 group animate-slide-up"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Upload className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold">{t('member.documents.uploadDocument')}</span>
          </button>
        </DialogTrigger>
        <DialogContent data-testid="document-dialog" className="bg-[#1a1a2e] border-white/10 rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">{t('member.documents.uploadDocument')}</DialogTitle>
            <DialogDescription className="text-white/50">
              {t('member.documents.uploadDesc') || 'Upload your important documents securely. Supported formats: PDF, JPG, PNG (max 5MB)'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="document_type" className="text-white/70 text-sm">{t('member.documents.documentType')}</Label>
              <Select value={formData.document_type} onValueChange={(value) => setFormData({ ...formData, document_type: value })}>
                <SelectTrigger data-testid="document-type-select" className="bg-white/5 border-white/10 text-white rounded-xl">
                  <SelectValue placeholder={t('member.documents.selectType') || 'Select document type'} />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-white/10">
                  <SelectItem value="passport">{t('member.documents.passport')}</SelectItem>
                  <SelectItem value="work_permit">{t('member.documents.visa')}</SelectItem>
                  <SelectItem value="contract">{t('member.documents.contract')}</SelectItem>
                  <SelectItem value="other">{t('member.documents.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="file" className="text-white/70 text-sm">{t('member.documents.selectFile') || 'Select File'}</Label>
              <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-cyan-500/50 transition-colors cursor-pointer"
                   onClick={() => document.getElementById('file')?.click()}>
                <Input
                  id="file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="document-file-input"
                />
                {formData.document_name ? (
                  <div>
                    <FileText className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                    <p className="text-white text-sm">{formData.document_name}</p>
                    <p className="text-white/40 text-xs mt-1">Click to change file</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
                    <p className="text-white/60 text-sm">Click to select a file</p>
                    <p className="text-white/40 text-xs mt-1">PDF, JPG, PNG (max 5MB)</p>
                  </div>
                )}
              </div>
            </div>
            <button 
              type="submit" 
              className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              disabled={uploading}
              data-testid="submit-document-button"
            >
              {uploading ? "Uploading..." : t('member.documents.uploadDocument')}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {documents.map((doc, idx) => (
          <div 
            key={doc.id} 
            className="glass-card p-5 animate-slide-up hover:bg-white/[0.08] transition-all duration-300"
            style={{ opacity: 0, animationDelay: `${(idx + 1) * 0.1}s` }}
            data-testid={`document-card-${doc.id}`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-2xl ${getDocumentIcon(doc.document_type)} flex items-center justify-center shrink-0 shadow-lg`}>
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white">{getDocumentTypeLabel(doc.document_type)}</h3>
                <p className="text-white/50 text-sm truncate">{doc.document_name}</p>
                <p className="text-white/30 text-xs mt-1">
                  {t('uploaded') || 'Uploaded'}: {new Date(doc.uploaded_at).toLocaleDateString()}
                </p>
                <span className={`inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-medium ${
                  doc.verified 
                    ? "bg-emerald-500/20 text-emerald-400" 
                    : "bg-amber-500/20 text-amber-400"
                }`}>
                  {doc.verified ? (t('verified') || "Verified") : (t('pendingVerification') || "Pending")}
                </span>
              </div>
              <button
                onClick={() => handleDelete(doc.id)}
                className="p-2 rounded-full hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-colors"
                data-testid={`delete-document-${doc.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {documents.length === 0 && (
          <div className="col-span-2 glass-card p-12 text-center animate-slide-up">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-white/30" />
            </div>
            <h3 className="text-white font-semibold mb-1">No Documents</h3>
            <p className="text-white/50 text-sm">{t('member.documents.noDocuments') || 'No documents uploaded yet'}</p>
            <p className="text-white/30 text-xs mt-2">{t('member.documents.uploadPrompt') || 'Upload your passport, work permit, or contract'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Chat View
function ChatView({ user, t }) {
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [otherUserPresence, setOtherUserPresence] = useState({ status: "offline", last_seen: null });
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(permission => {
        setNotificationPermission(permission);
      });
    }
  }, []);

  const showNotification = (title, options) => {
    if ("Notification" in window && Notification.permission === "granted") {
      const notification = new Notification(title, options);
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  useEffect(() => {
    fetchChatRooms();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.id);
      fetchPresence(selectedRoom.id);
      connectWebSocket(selectedRoom.id);
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChatRooms = async () => {
    try {
      const response = await axios.get('/chat/rooms');
      setChatRooms(response.data);
      if (response.data.length > 0) {
        setSelectedRoom(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to load chat rooms");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await axios.get(`/chat/rooms/${roomId}/messages`);
      setMessages(response.data);
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const fetchPresence = async (roomId) => {
    try {
      const response = await axios.get(`/chat/rooms/${roomId}/presence`);
      // Member sees staff presence
      setOtherUserPresence(response.data.staff);
    } catch (error) {
      console.error("Failed to fetch presence");
    }
  };

  const connectWebSocket = useCallback((roomId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/chat/${roomId}?token=${token}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'message') {
        setMessages(prev => [...prev, data]);
        
        // Send browser notification if message is from other user and tab is not focused
        if (data.sender_id !== user.id && document.hidden) {
          showNotification("New Message", {
            body: data.content?.substring(0, 100) || "You received a new message",
            icon: "/favicon.ico",
            tag: `message-${data.id}`
          });
        }
      } else if (data.type === 'presence') {
        // Update presence if it's the other user
        if (data.user_id !== user.id) {
          setOtherUserPresence({ status: data.status, last_seen: data.last_seen });
        }
      } else if (data.type === 'connected' && data.other_user_presence) {
        setOtherUserPresence(data.other_user_presence);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    wsRef.current = ws;
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;

    setSending(true);
    try {
      // Send via REST API (WebSocket will broadcast)
      await axios.post(`/chat/rooms/${selectedRoom.id}/messages`, {
        content: newMessage.trim(),
        message_type: "text"
      });
      setNewMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedRoom) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await axios.post(`/chat/rooms/${selectedRoom.id}/upload`, {
            file_name: file.name,
            file_data: reader.result,
            file_type: file.type
          });
          toast.success("File sent successfully");
        } catch (error) {
          toast.error("Failed to upload file");
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to read file");
      setUploading(false);
    }
    
    // Reset input
    e.target.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderMessage = (msg) => {
    const isOwn = msg.sender_id === user.id;
    
    if (msg.message_type === 'image') {
      return (
        <div className={`max-w-[70%] rounded-2xl overflow-hidden ${
          isOwn ? 'bg-slate-900 rounded-br-md' : 'bg-white border border-slate-200 rounded-bl-md'
        }`}>
          <div className="p-2">
            <div className="rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
              <Image className="w-8 h-8 text-slate-400" />
            </div>
            <p className={`text-xs mt-2 ${isOwn ? 'text-slate-300' : 'text-slate-500'}`}>
              {msg.file_name}
            </p>
          </div>
          <div className={`px-3 py-2 ${isOwn ? '' : 'border-t'}`}>
            <p className={`text-xs ${isOwn ? 'text-slate-300' : 'text-slate-400'}`}>
              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      );
    }
    
    if (msg.message_type === 'file') {
      return (
        <div className={`max-w-[70%] rounded-2xl px-4 py-3 ${
          isOwn ? 'bg-slate-900 text-white rounded-br-md' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOwn ? 'bg-slate-700' : 'bg-slate-100'}`}>
              <File className={`w-5 h-5 ${isOwn ? 'text-slate-300' : 'text-slate-500'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{msg.file_name}</p>
              <p className={`text-xs ${isOwn ? 'text-slate-400' : 'text-slate-500'}`}>
                {formatFileSize(msg.file_size)}
              </p>
            </div>
          </div>
          <p className={`text-xs mt-2 ${isOwn ? 'text-slate-300' : 'text-slate-400'}`}>
            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      );
    }
    
    // Default text message
    return (
      <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
        isOwn ? 'bg-slate-900 text-white rounded-br-md' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'
      }`}>
        <p className="text-sm">{msg.content}</p>
        <p className={`text-xs mt-1 ${isOwn ? 'text-slate-300' : 'text-slate-400'}`}>
          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    );
  };

  if (loading) {
    return <div className="p-6">Loading chat...</div>;
  }

  if (chatRooms.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-slate-900">{t('member.chat.title')}</h2>
        <div className="text-center py-12 text-slate-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{t('member.chat.noChat') || 'No active chat available'}</p>
          <p className="text-sm mt-2">{t('member.chat.noChatDesc') || 'You will be able to chat once a staff member is assigned to your case.'}</p>
        </div>
      </div>
    );
  }

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return "Never";
    const date = new Date(lastSeen);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-4" data-testid="chat-view">
      <h2 className="text-2xl font-bold text-slate-900">{t('member.chat.title')}</h2>
      
      <Card className="border-none shadow-sm overflow-hidden">
        <div className="flex h-[500px]">
          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {/* Chat Header */}
            <div className="bg-slate-50 border-b px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-blue-600 text-white font-semibold">
                      {selectedRoom?.staff?.name?.split(" ").map(n => n[0]).join("").substring(0, 2) || "ST"}
                    </AvatarFallback>
                  </Avatar>
                  {/* Presence Indicator */}
                  <span 
                    className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                      otherUserPresence.status === "online" ? "bg-emerald-500" : "bg-slate-400"
                    }`}
                    data-testid="presence-indicator"
                  />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{selectedRoom?.staff?.name || "Support Staff"}</p>
                  <p className="text-xs text-slate-500">
                    {otherUserPresence.status === "online" 
                      ? <span className="text-emerald-600 font-medium">Online</span>
                      : `Last seen ${formatLastSeen(otherUserPresence.last_seen)}`
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" data-testid="messages-container">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${msg.id}`}
                >
                  {renderMessage(msg)}
                </div>
              ))}
              {messages.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <p>No messages yet. Start the conversation!</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSendMessage} className="border-t bg-white p-4">
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  data-testid="file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !selectedRoom}
                  data-testid="attach-file-button"
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={uploading ? "Uploading file..." : "Type your message..."}
                  className="flex-1"
                  disabled={sending || uploading}
                  data-testid="message-input"
                />
                <Button type="submit" disabled={sending || uploading || !newMessage.trim()} data-testid="send-message-button">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Announcements View
function AnnouncementsView({ user, t }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'videos'
  const { translateTexts, translating } = useContentTranslation();
  const [translatedContent, setTranslatedContent] = useState({});
  const [currentLang, setCurrentLang] = useState(getSavedLanguage());

  // Poll for language changes
  useEffect(() => {
    const checkLanguage = () => {
      const lang = getSavedLanguage();
      if (lang !== currentLang) {
        setCurrentLang(lang);
        setTranslatedContent({}); // Clear translations to trigger re-fetch
      }
    };
    const interval = setInterval(checkLanguage, 500);
    return () => clearInterval(interval);
  }, [currentLang]);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Auto-translate announcements content when they change or language changes
  useEffect(() => {
    if (currentLang !== 'en' && announcements.length > 0) {
      const textsToTranslate = [];
      announcements.forEach(ann => {
        if (ann.title) textsToTranslate.push(ann.title);
        if (ann.body) textsToTranslate.push(ann.body);
      });
      
      if (textsToTranslate.length > 0) {
        translateTexts(textsToTranslate).then(translations => {
          setTranslatedContent(translations);
        });
      }
    } else if (currentLang === 'en') {
      setTranslatedContent({});
    }
  }, [announcements, currentLang, translateTexts]);

  const getTranslated = (text) => {
    if (currentLang === 'en' || !text) return text;
    return translatedContent[text] || text;
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get('/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      warning: 'border-l-red-500 bg-red-50',
      info: 'border-l-blue-500 bg-blue-50',
      update: 'border-l-emerald-500 bg-emerald-50',
      video: 'border-l-purple-500 bg-purple-50'
    };
    return colors[category] || 'border-l-slate-500 bg-slate-50';
  };

  const getCategoryBadge = (category) => {
    const badges = {
      warning: 'bg-red-100 text-red-700',
      info: 'bg-blue-100 text-blue-700',
      update: 'bg-emerald-100 text-emerald-700',
      video: 'bg-purple-100 text-purple-700'
    };
    return badges[category] || 'bg-slate-100 text-slate-700';
  };

  const getCategoryLabel = (category) => {
    return t(`member.announcements.categories.${category}`) || category;
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const shareOnWhatsApp = (announcement) => {
    const text = `${announcement.title}\n\n${announcement.body.substring(0, 200)}...`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Detail View
  if (selectedAnnouncement) {
    const youtubeId = extractYouTubeId(selectedAnnouncement.video_url);
    
    return (
      <div className="space-y-6" data-testid="announcement-detail-view">
        <button
          onClick={() => setSelectedAnnouncement(null)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
          data-testid="back-to-announcements"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          {t('member.announcements.backToList')}
        </button>

        <Card className="rounded-sm border-slate-200 shadow-sm overflow-hidden">
          {/* Header */}
          <div className={`p-6 border-l-4 ${getCategoryColor(selectedAnnouncement.category)}`}>
            <div className="flex items-center gap-3 mb-3">
              <Badge className={`rounded-sm font-semibold ${getCategoryBadge(selectedAnnouncement.category)}`}>
                {getCategoryLabel(selectedAnnouncement.category)}
              </Badge>
              {selectedAnnouncement.is_pinned && (
                <span className="text-sm font-medium text-amber-600 flex items-center gap-1">
                  📌 {t('member.announcements.pinned')}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{getTranslated(selectedAnnouncement.title)}</h1>
            <p className="text-sm text-slate-500">
              {t('member.announcements.postedOn')} {formatDate(selectedAnnouncement.created_at)}
            </p>
          </div>

          {/* Media Content */}
          {youtubeId && (
            <div className="aspect-video bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}`}
                className="w-full h-full"
                allowFullScreen
                title={selectedAnnouncement.title}
              />
            </div>
          )}
          
          {selectedAnnouncement.video_url && !youtubeId && (
            <div className="aspect-video bg-black">
              <video 
                src={selectedAnnouncement.video_url} 
                controls 
                className="w-full h-full"
              />
            </div>
          )}

          {selectedAnnouncement.image_url && !selectedAnnouncement.video_url && (
            <div className="bg-slate-100">
              <img 
                src={selectedAnnouncement.image_url} 
                alt={selectedAnnouncement.title}
                className="w-full max-h-[400px] object-contain"
              />
            </div>
          )}

          {/* Body Content */}
          <CardContent className="p-6">
            <div className="prose prose-slate max-w-none">
              <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                {getTranslated(selectedAnnouncement.body)}
              </p>
            </div>
          </CardContent>

          {/* Actions */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={() => shareOnWhatsApp(selectedAnnouncement)}
              className="rounded-sm flex items-center gap-2"
              data-testid="share-whatsapp-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-green-600">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t('member.announcements.shareWhatsApp')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Filter announcements based on active tab
  const filteredAnnouncements = activeTab === 'videos' 
    ? announcements.filter(a => a.category === 'video')
    : announcements;

  // Separate pinned and regular announcements
  const pinnedAnnouncements = filteredAnnouncements.filter(a => a.is_pinned);
  const regularAnnouncements = filteredAnnouncements.filter(a => !a.is_pinned);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="border-b-2 border-slate-200 pb-4">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{t('member.announcements.title')}</h2>
        </div>
        <div className="text-center py-12 text-slate-500">
          <div className="animate-spin w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full mx-auto mb-4"></div>
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-view">
      <div className="border-b-2 border-slate-200 pb-4">
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{t('member.announcements.title')}</h2>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 font-semibold text-sm border-b-2 transition-colors ${
            activeTab === 'all' 
              ? 'border-slate-900 text-slate-900' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-all-announcements"
        >
          {t('member.announcements.allAnnouncements')}
        </button>
        <button
          onClick={() => setActiveTab('videos')}
          className={`px-4 py-2 font-semibold text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'videos' 
              ? 'border-slate-900 text-slate-900' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-video-awareness"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
          {t('member.announcements.videoAwareness')}
        </button>
      </div>

      {/* Video Awareness Description */}
      {activeTab === 'videos' && (
        <Card className="rounded-sm border-purple-200 bg-purple-50 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-600"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
            </div>
            <div>
              <h3 className="font-bold text-purple-900">{t('member.announcements.videoAwareness')}</h3>
              <p className="text-sm text-purple-700">{t('member.announcements.videoAwarenessDesc')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Announcements */}
      {filteredAnnouncements.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 opacity-50"><path d="M3 11l18-5v12L3 14v-3z"></path><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path></svg>
          <p>{activeTab === 'videos' ? t('member.announcements.noVideos') : t('member.announcements.noAnnouncements')}</p>
        </div>
      )}

      {/* Pinned Announcements */}
      {pinnedAnnouncements.length > 0 && (
        <div className="space-y-4">
          {pinnedAnnouncements.map((announcement) => (
            <AnnouncementCard 
              key={announcement.id}
              announcement={announcement}
              t={t}
              getCategoryColor={getCategoryColor}
              getCategoryBadge={getCategoryBadge}
              getCategoryLabel={getCategoryLabel}
              extractYouTubeId={extractYouTubeId}
              formatDate={formatDate}
              onClick={() => setSelectedAnnouncement(announcement)}
              getTranslated={getTranslated}
            />
          ))}
        </div>
      )}

      {/* Regular Announcements */}
      {regularAnnouncements.length > 0 && (
        <div className="space-y-4">
          {regularAnnouncements.map((announcement) => (
            <AnnouncementCard 
              key={announcement.id}
              announcement={announcement}
              t={t}
              getCategoryColor={getCategoryColor}
              getCategoryBadge={getCategoryBadge}
              getCategoryLabel={getCategoryLabel}
              extractYouTubeId={extractYouTubeId}
              formatDate={formatDate}
              onClick={() => setSelectedAnnouncement(announcement)}
              getTranslated={getTranslated}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Announcement Card Component
function AnnouncementCard({ announcement, t, getCategoryColor, getCategoryBadge, getCategoryLabel, extractYouTubeId, formatDate, onClick, getTranslated }) {
  const youtubeId = extractYouTubeId(announcement.video_url);
  const hasThumbnail = announcement.image_url || youtubeId;

  return (
    <Card 
      className={`rounded-sm border-slate-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow border-l-4 ${getCategoryColor(announcement.category)}`}
      onClick={onClick}
      data-testid={`announcement-card-${announcement.id}`}
    >
      <div className="flex">
        {/* Thumbnail */}
        {hasThumbnail && (
          <div className="w-32 sm:w-48 flex-shrink-0 bg-slate-100">
            {youtubeId ? (
              <img 
                src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
                alt={announcement.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <img 
                src={announcement.image_url}
                alt={announcement.title}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className={`rounded-sm font-semibold text-xs ${getCategoryBadge(announcement.category)}`}>
              {getCategoryLabel(announcement.category)}
            </Badge>
            {announcement.is_pinned && (
              <span className="text-xs font-medium text-amber-600 flex items-center gap-1">
                📌 {t('member.announcements.pinned')}
              </span>
            )}
          </div>
          
          <h3 className="font-bold text-slate-900 mb-1 line-clamp-2">{getTranslated ? getTranslated(announcement.title) : announcement.title}</h3>
          
          <p className="text-sm text-slate-500 mb-2">
            {t('member.announcements.postedOn')} {formatDate(announcement.created_at)}
          </p>
          
          <p className="text-sm text-slate-600 line-clamp-2">{getTranslated ? getTranslated(announcement.body) : announcement.body}</p>
          
          <button 
            className="mt-3 text-sm font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1"
            data-testid={`read-more-${announcement.id}`}
          >
            {t('member.announcements.readMore')}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>
      </div>
    </Card>
  );
}

// Settings View with Language Selection
function SettingsView({ user, t }) {
  const [selectedLanguage, setSelectedLanguage] = useState(getSavedLanguage());
  const [saving, setSaving] = useState(false);

  const LANGUAGES = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "ms", name: "Bahasa Melayu", flag: "🇲🇾" },
    { code: "ta", name: "தமிழ் (Tamil)", flag: "🇮🇳" },
    { code: "hi", name: "हिन्दी (Hindi)", flag: "🇮🇳" },
    { code: "bn", name: "বাংলা (Bengali)", flag: "🇧🇩" },
    { code: "ne", name: "नेपाली (Nepali)", flag: "🇳🇵" },
    { code: "my", name: "မြန်မာ (Burmese)", flag: "🇲🇲" },
  ];

  const handleLanguageChange = (langCode) => {
    setSaving(true);
    localStorage.setItem('myshield_lang', langCode);
    setSelectedLanguage(langCode);
    window.dispatchEvent(new Event('languageChange'));
    toast.success("Language updated!");
    setSaving(false);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  return (
    <div className="space-y-6" data-testid="settings-view">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white tracking-tight">{t('member.settings.title') || 'Settings'}</h2>
        <p className="text-white/50 text-sm mt-1">Customize your app experience</p>
      </div>
      
      {/* Language Selection */}
      <div className="glass-card p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">{t('member.settings.language') || 'Language'}</h3>
            <p className="text-white/50 text-sm">{t('member.settings.languageDesc') || 'Choose your preferred language'}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {LANGUAGES.map((lang, idx) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              disabled={saving}
              className={`p-4 rounded-2xl text-left transition-all flex items-center gap-3 animate-slide-up ${
                selectedLanguage === lang.code
                  ? 'bg-gradient-to-br from-cyan-500/20 to-blue-600/20 ring-2 ring-cyan-500/50'
                  : 'bg-white/5 hover:bg-white/10'
              }`}
              style={{ opacity: 0, animationDelay: `${(idx + 1) * 0.05}s` }}
              data-testid={`lang-${lang.code}`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <span className="font-medium text-white">{lang.name}</span>
              {selectedLanguage === lang.code && (
                <CheckCircle className="w-5 h-5 text-cyan-400 ml-auto" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Account Information */}
      <div className="glass-card overflow-hidden animate-slide-up stagger-1" style={{ opacity: 0 }}>
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">{t('member.settings.accountInfo') || 'Account Information'}</h3>
              <p className="text-white/50 text-sm">Your account details</p>
            </div>
          </div>
        </div>
        <div className="divide-y divide-white/5">
          <div className="flex justify-between items-center p-4">
            <span className="text-white/50 text-sm">{t('member.settings.email') || 'Email'}</span>
            <span className="text-white font-medium">{user.email}</span>
          </div>
          <div className="flex justify-between items-center p-4">
            <span className="text-white/50 text-sm">{t('member.settings.memberId') || 'Member ID'}</span>
            <span className="text-white/70 font-mono text-sm">{user.id?.substring(0, 8)}...</span>
          </div>
          <div className="flex justify-between items-center p-4">
            <span className="text-white/50 text-sm">{t('member.settings.accountStatus') || 'Status'}</span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              user.subscription_status === "active" 
                ? "bg-emerald-500/20 text-emerald-400" 
                : "bg-amber-500/20 text-amber-400"
            }`}>
              {user.subscription_status === "active" ? "Active" : "Pending"}
            </span>
          </div>
        </div>
      </div>

      {/* App Info */}
      <div className="glass-card p-6 animate-slide-up stagger-2" style={{ opacity: 0 }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">MyShield App</h3>
            <p className="text-white/50 text-sm">Version 2.0</p>
          </div>
        </div>
        <p className="text-white/40 text-xs">
          Protection for Every Worker • &copy; 2024 MyShield
        </p>
      </div>
    </div>
  );
}

// Payment Gate Component - Shows when member hasn't paid yet
function PaymentGate({ user, onLogout, t, onPaymentComplete }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [manualPaymentData, setManualPaymentData] = useState({
    reference_number: "",
    proof_image: ""
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleStripePayment = async (method) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post('/payment/create-session', {
        package_id: "1", // 1 month = RM 20
        payment_method: method,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create payment session");
      setProcessingPayment(false);
    }
  };

  const handleManualPaymentSubmit = async () => {
    if (!manualPaymentData.reference_number) {
      toast.error("Please enter payment reference number");
      return;
    }
    
    setProcessingPayment(true);
    try {
      const response = await axios.post('/payment/create-session', {
        package_id: "1", // 1 month = RM 20
        payment_method: selectedMethod,
        origin_url: window.location.origin
      });
      await axios.post('/payment/submit-manual', {
        transaction_id: response.data.transaction_id,
        reference_number: manualPaymentData.reference_number,
        proof_image: manualPaymentData.proof_image
      });
      toast.success("Payment submitted for verification!");
      // Update user state to show pending approval
      const updatedUser = { ...user, subscription_status: "pending" };
      onPaymentComplete(updatedUser);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error("Please upload a JPG or PNG image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setManualPaymentData(prev => ({ ...prev, proof_image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0A1128] to-black flex items-center justify-center p-4">
      <div className="max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/30">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Complete Your Registration</h1>
          <p className="text-white/60">Pay the membership fee to access all MyShield features</p>
        </div>

        {/* User Info Card */}
        <div className="glass-card p-5">
          <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14 ring-2 ring-cyan-500/30">
              <AvatarImage src={user.profile_photo} alt={user.full_name} />
              <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-lg font-bold">
                {user.full_name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-white">{user.full_name}</p>
              <p className="text-white/50 text-sm">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Fee Display */}
        <div className="glass-card overflow-hidden">
          <div className="bg-gradient-to-br from-emerald-500/20 via-cyan-600/10 to-blue-500/20 p-6 text-center">
            <p className="text-white/60 text-sm uppercase tracking-wide font-medium mb-2">Membership Fee</p>
            <p className="text-5xl font-bold text-white">
              <span className="text-xl font-normal text-white/60">RM</span> 20
            </p>
            <p className="text-white/50 text-sm mt-2">One-time registration fee</p>
          </div>
        </div>

        {/* Payment Methods */}
        {!selectedMethod ? (
          <div className="space-y-3">
            <p className="text-white/60 text-sm font-medium mb-4">Choose payment method</p>
            
            {/* Card Payment */}
            <button
              onClick={() => handleStripePayment("fpx")}
              disabled={processingPayment}
              className="w-full glass-card p-5 flex items-center gap-4 text-left hover:bg-white/10 transition-all duration-300 disabled:opacity-50 group"
              data-testid="payment-gate-card"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-105 transition-transform">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-white">Card Payment</div>
                <div className="text-sm text-white/50">Pay with debit/credit card</div>
              </div>
              <div className="text-xl font-bold text-white">RM 20</div>
            </button>

            {/* Auto-Debit Subscription */}
            <button
              onClick={() => handleStripePayment("subscription")}
              disabled={processingPayment}
              className="w-full glass-card p-5 flex items-center gap-4 text-left hover:bg-white/10 transition-all duration-300 disabled:opacity-50 group"
              data-testid="payment-gate-subscription"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-500/20 group-hover:scale-105 transition-transform">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-white">Auto-Debit Monthly</div>
                <div className="text-sm text-white/50">Automatic monthly payment</div>
              </div>
              <div className="text-xl font-bold text-white">RM 20<span className="text-sm text-white/50">/mo</span></div>
            </button>

            {/* DuitNow QR */}
            <button
              onClick={() => setSelectedMethod("qr_code")}
              disabled={processingPayment}
              className="w-full glass-card p-5 flex items-center gap-4 text-left hover:bg-white/10 transition-all duration-300 disabled:opacity-50 group"
              data-testid="payment-gate-qr"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg shadow-pink-500/20 group-hover:scale-105 transition-transform">
                <QrCode className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-white">DuitNow QR</div>
                <div className="text-sm text-white/50">Scan and pay with any banking app</div>
              </div>
              <div className="text-xl font-bold text-white">RM 20</div>
            </button>

            {/* Bank Transfer */}
            <button
              onClick={() => setSelectedMethod("bank_transfer")}
              disabled={processingPayment}
              className="w-full glass-card p-5 flex items-center gap-4 text-left hover:bg-white/10 transition-all duration-300 disabled:opacity-50 group"
              data-testid="payment-gate-bank"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20 group-hover:scale-105 transition-transform">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-white">Bank Transfer</div>
                <div className="text-sm text-white/50">Manual transfer to bank account</div>
              </div>
              <div className="text-xl font-bold text-white">RM 20</div>
            </button>
          </div>
        ) : selectedMethod === "qr_code" ? (
          /* QR Code Payment */
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">DuitNow QR Payment</h3>
              <button onClick={() => setSelectedMethod(null)} className="text-white/60 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-center mb-6">
              <div className="inline-block p-4 bg-white rounded-2xl shadow-lg">
                <img 
                  src={`${BACKEND_URL}/api/payment/qr-code`} 
                  alt="DuitNow QR Code" 
                  className="w-48 h-48"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/200x200?text=QR+Code";
                  }}
                />
              </div>
              <p className="text-white/60 text-sm mt-4">Scan this QR code with your banking app</p>
              <p className="text-2xl font-bold text-white mt-2">RM 20.00</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white/80">Payment Reference Number</Label>
                <Input
                  value={manualPaymentData.reference_number}
                  onChange={(e) => setManualPaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
                  placeholder="Enter your payment reference"
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>
              <div>
                <Label className="text-white/80">Payment Proof (Optional)</Label>
                <div className="mt-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="proof-upload-gate"
                  />
                  <label
                    htmlFor="proof-upload-gate"
                    className="flex items-center gap-2 p-3 border border-dashed border-white/20 rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                  >
                    <Upload className="w-5 h-5 text-white/60" />
                    <span className="text-white/60 text-sm">
                      {manualPaymentData.proof_image ? "Image uploaded ✓" : "Upload screenshot"}
                    </span>
                  </label>
                </div>
              </div>
              <Button
                onClick={handleManualPaymentSubmit}
                disabled={processingPayment || !manualPaymentData.reference_number}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3"
              >
                {processingPayment ? "Submitting..." : "Submit Payment"}
              </Button>
            </div>
          </div>
        ) : (
          /* Bank Transfer */
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Bank Transfer</h3>
              <button onClick={() => setSelectedMethod(null)} className="text-white/60 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="bg-white/5 rounded-xl p-5 mb-6 space-y-3">
              <div className="flex justify-between">
                <span className="text-white/60">Bank</span>
                <span className="text-white font-medium">Maybank</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Account Name</span>
                <span className="text-white font-medium">MyShield Sdn Bhd</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Account Number</span>
                <span className="text-white font-medium font-mono">5621 8765 4321</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-white/10">
                <span className="text-white/60">Amount</span>
                <span className="text-2xl font-bold text-white">RM 20.00</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-white/80">Payment Reference Number</Label>
                <Input
                  value={manualPaymentData.reference_number}
                  onChange={(e) => setManualPaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
                  placeholder="Enter your payment reference"
                  className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                />
              </div>
              <div>
                <Label className="text-white/80">Payment Proof (Optional)</Label>
                <div className="mt-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="bank-proof-upload-gate"
                  />
                  <label
                    htmlFor="bank-proof-upload-gate"
                    className="flex items-center gap-2 p-3 border border-dashed border-white/20 rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                  >
                    <Upload className="w-5 h-5 text-white/60" />
                    <span className="text-white/60 text-sm">
                      {manualPaymentData.proof_image ? "Image uploaded ✓" : "Upload screenshot"}
                    </span>
                  </label>
                </div>
              </div>
              <Button
                onClick={handleManualPaymentSubmit}
                disabled={processingPayment || !manualPaymentData.reference_number}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-3"
              >
                {processingPayment ? "Submitting..." : "Submit Payment"}
              </Button>
            </div>
          </div>
        )}

        {/* Sign Out Button - Only option until approved */}
        <Button 
          variant="ghost" 
          onClick={onLogout} 
          className="w-full text-white/60 hover:text-white hover:bg-white/5"
          data-testid="payment-gate-logout"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}

export default function MemberDashboard({ user: initialUser, onLogout }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState(getSavedLanguage());
  const [user, setUser] = useState(initialUser);
  const [unreadCount, setUnreadCount] = useState(0);
  const t = (path) => getTranslation(lang, path);

  // Handle user updates (e.g., photo upload)
  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    // Also update localStorage
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Listen for language changes
  useEffect(() => {
    const handleStorageChange = () => {
      setLang(getSavedLanguage());
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  // Fetch unread announcements count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get('/announcements/unread-count');
        setUnreadCount(response.data.unread_count);
      } catch (error) {
        console.error('Failed to fetch unread count', error);
      }
    };
    fetchUnreadCount();
    // Refresh every minute
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Check if member needs to pay first (payment not completed)
  const needsPayment = user.payment_completed === false;

  // Payment Gate - Block access until payment is completed
  if (needsPayment) {
    return <PaymentGate user={user} onLogout={onLogout} t={t} onPaymentComplete={handleUserUpdate} />;
  }

  // Check if member is pending approval (payment done but subscription not yet activated by admin)
  const isPendingApproval = user.subscription_status === "pending";

  // Show waiting screen if pending approval
  if (isPendingApproval) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-none shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <CardTitle className="text-2xl text-slate-900">Membership Pending</CardTitle>
            <CardDescription className="text-base mt-2">
              Your account is being reviewed
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              Thank you for registering with MyShield. Your membership is currently pending approval.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2">
              <p className="text-sm text-slate-600">
                <strong>What happens next:</strong>
              </p>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>Admin will verify your payment</li>
                <li>A dedicated agent will be assigned to you</li>
                <li>You will receive full access to all features</li>
              </ul>
            </div>
            <div className="pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-4">
                This usually takes 1-2 business days. If you have any questions, please contact us.
              </p>
              <Button variant="outline" onClick={onLogout} className="w-full" data-testid="pending-logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const navItems = [
    { path: "/dashboard/profile", label: t('member.nav.profile'), icon: User, testId: "nav-profile" },
    { path: "/dashboard/sos", label: t('member.nav.sos'), icon: Siren, testId: "nav-sos", isAlert: true },
    { path: "/dashboard/vacancies", label: t('member.nav.vacancies'), icon: Briefcase, testId: "nav-vacancies" },
    { path: "/dashboard/announcements", label: t('member.nav.announcements'), icon: Megaphone, testId: "nav-announcements", badge: unreadCount > 0 ? unreadCount : null },
    { path: "/dashboard/complaints", label: t('member.nav.complaints'), icon: AlertCircle, testId: "nav-member-complaints" },
    { path: "/dashboard/payments", label: t('member.nav.payments'), icon: DollarSign, testId: "nav-member-payments" },
    { path: "/dashboard/documents", label: t('member.nav.documents'), icon: FileText, testId: "nav-member-documents" },
    { path: "/dashboard/chat", label: t('member.nav.messages'), icon: MessageSquare, testId: "nav-member-chat" },
    { path: "/dashboard/settings", label: t('member.nav.settings'), icon: Settings, testId: "nav-member-settings" },
  ];

  const isActiveRoute = (path) => {
    if (path === "/dashboard/profile") {
      return location.pathname === "/dashboard" || location.pathname.includes("/profile");
    }
    return location.pathname.includes(path.split("/").pop());
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0A1128] to-black">
      {/* Desktop Header - Glassmorphism */}
      <header className="hidden md:block sticky top-0 z-50 glass-nav border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-4 group">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
                alt="MyShield Logo" 
                className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-white tracking-tight">MyShield</span>
              <span className="text-xs text-cyan-400 font-medium uppercase tracking-wider">Protection For Every Worker</span>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="flex items-center gap-1">
            {navItems.slice(0, 6).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActiveRoute(item.path)
                    ? item.isAlert 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-white/10 text-white'
                    : item.isAlert
                      ? 'text-red-400/70 hover:text-red-400 hover:bg-red-500/10'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={item.testId}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden lg:inline">{item.label}</span>
                {item.badge && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-lg glow-red">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </Link>
            ))}
            <div className="w-px h-6 bg-white/10 mx-2" />
            {navItems.slice(6).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActiveRoute(item.path)
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={item.testId}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold text-white">{user.full_name}</p>
              <span className="text-xs text-white/50">Member</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onLogout} 
              data-testid="member-logout-button" 
              className="text-white/60 hover:text-white hover:bg-white/10 rounded-full px-4"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {t('logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Header - Minimal */}
      <header className="md:hidden sticky top-0 z-50 glass-nav border-b border-white/10">
        <div className="px-4 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
              alt="MyShield Logo" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-lg font-bold text-white">MyShield</span>
          </Link>
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8 ring-2 ring-cyan-400/50">
              {user.profile_photo ? (
                <AvatarImage src={user.profile_photo} alt={user.full_name} />
              ) : null}
              <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-sm font-bold">
                {user.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </header>

      {/* Main Content with padding for mobile bottom nav */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 md:pb-8">
        <div className="animate-fade-in">
          <Routes>
            <Route path="/" element={<ProfileView user={user} t={t} onUserUpdate={handleUserUpdate} />} />
            <Route path="/profile" element={<ProfileView user={user} t={t} onUserUpdate={handleUserUpdate} />} />
            <Route path="/sos" element={<SOSView user={user} t={t} />} />
            <Route path="/vacancies" element={<VacanciesView user={user} t={t} />} />
            <Route path="/announcements" element={<AnnouncementsView user={user} t={t} />} />
            <Route path="/complaints" element={<ComplaintsView user={user} t={t} />} />
            <Route path="/payments" element={<PaymentsView user={user} t={t} />} />
            <Route path="/documents" element={<DocumentsView user={user} t={t} />} />
            <Route path="/chat" element={<ChatView user={user} t={t} />} />
            <Route path="/settings" element={<SettingsView user={user} t={t} />} />
          </Routes>
        </div>
      </main>

      {/* Mobile Floating Bottom Navigation - Apple HIG Style */}
      <nav className="md:hidden fixed bottom-6 left-4 right-4 glass-nav rounded-full z-50 safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.slice(0, 5).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative flex flex-col items-center justify-center w-14 h-14 rounded-full transition-all duration-300 active:scale-95 ${
                isActiveRoute(item.path)
                  ? item.isAlert 
                    ? 'bg-red-500/20 text-red-400' 
                    : 'bg-white/15 text-white'
                  : item.isAlert
                    ? 'text-red-400/60'
                    : 'text-white/40'
              }`}
              data-testid={`mobile-${item.testId}`}
            >
              <item.icon className={`w-6 h-6 ${isActiveRoute(item.path) && item.isAlert ? 'drop-shadow-[0_0_8px_rgba(255,59,48,0.8)]' : ''}`} strokeWidth={1.5} />
              {item.badge && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </Link>
          ))}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex flex-col items-center justify-center w-14 h-14 rounded-full text-white/40 hover:text-white/60 transition-colors active:scale-95"
            data-testid="mobile-more-button"
          >
            <Menu className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>
      </nav>

      {/* Mobile More Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="absolute bottom-24 left-4 right-4 glass-card p-4 rounded-3xl animate-slide-up">
            <div className="grid grid-cols-4 gap-4 mb-4">
              {navItems.slice(5).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex flex-col items-center justify-center p-3 rounded-2xl transition-all active:scale-95 ${
                    isActiveRoute(item.path)
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:bg-white/5'
                  }`}
                  data-testid={`mobile-more-${item.testId}`}
                >
                  <item.icon className="w-6 h-6 mb-1" strokeWidth={1.5} />
                  <span className="text-xs font-medium">{item.label}</span>
                </Link>
              ))}
            </div>
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 ring-2 ring-cyan-400/30">
                    {user.profile_photo ? (
                      <AvatarImage src={user.profile_photo} alt={user.full_name} />
                    ) : null}
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white font-bold">
                      {user.full_name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-semibold text-white">{user.full_name}</p>
                    <p className="text-xs text-white/50">Member</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onLogout}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-full"
                  data-testid="mobile-logout-button"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}